/**
 * Info Popover - Global popover system
 *
 * A reusable popover component that positions itself globally
 * and can be triggered from any element on the page.
 */

if (!customElements.get("info-popover")) {
  customElements.define(
    "info-popover",
    class InfoPopover extends HTMLElement {
      constructor() {
        super();

        this.popoverId = this.dataset.popoverId;
        this.trigger = this.querySelector("[data-popover-trigger]");
        this.popup = null;
        this.container = null;

        if (!this.trigger || !this.popoverId) return;

        // Media query for desktop hover support
        this.mql = window.matchMedia("(min-width: 750px)");

        this.init();
      }

      init() {
        this.container = document.getElementById("info-popover-container");
        if (!this.container) return;

        const template = document.querySelector(
          `template[data-popover-content="${this.popoverId}"]`
        );
        if (template) {
          const clone = template.content.cloneNode(true);
          this.container.appendChild(clone);
          this.popup = this.container.querySelector(
            `[data-popover="${this.popoverId}"]`
          );
          template.remove();
        }

        if (!this.popup) return;

        this.setupEventListeners();
      }

      setupEventListeners() {
        this.trigger.addEventListener("mouseenter", this.open.bind(this));
        this.trigger.addEventListener(
          "mouseleave",
          this.scheduleClose.bind(this)
        );
        this.popup.addEventListener("mouseenter", this.cancelClose.bind(this));
        this.popup.addEventListener("mouseleave", this.close.bind(this));

        this.trigger.addEventListener("click", (e) => e.preventDefault());

        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && !this.popup.hasAttribute("hidden")) {
            this.close();
          }
        });

        document.addEventListener("click", (e) => {
          if (
            !this.popup.hasAttribute("hidden") &&
            !this.trigger.contains(e.target) &&
            !this.popup.contains(e.target)
          ) {
            this.close();
          }
        });

        this.repositionHandler = () => {
          if (!this.popup.hasAttribute("hidden")) {
            this.positionPopup();
          }
        };
        window.addEventListener("scroll", this.repositionHandler, {
          passive: true,
        });
        window.addEventListener("resize", this.repositionHandler);
      }

      disconnectedCallback() {
        if (this.popup) {
          this.popup.remove();
        }
        if (this.repositionHandler) {
          window.removeEventListener("scroll", this.repositionHandler);
          window.removeEventListener("resize", this.repositionHandler);
        }
      }

      toggle(event) {
        event.preventDefault();
        event.stopPropagation();

        const isOpen = this.trigger.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      positionPopup() {
        if (!this.trigger || !this.popup) return;

        const triggerRect = this.trigger.getBoundingClientRect();
        const popupRect = this.popup.getBoundingClientRect();
        const popupContent = this.popup.querySelector(".info-popover__content");
        const gap = 12;
        const spaceAbove = triggerRect.top;
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const popupHeight = popupRect.height;

        let top;
        let position = "above";

        if (spaceAbove >= popupHeight + gap || spaceAbove > spaceBelow) {
          top = triggerRect.top - popupHeight - gap;
          position = "above";
        } else {
          top = triggerRect.bottom + gap;
          position = "below";
        }

        top = Math.max(
          gap,
          Math.min(top, window.innerHeight - popupHeight - gap)
        );

        const triggerCenter = triggerRect.left + triggerRect.width / 2;
        const idealLeft = triggerCenter - popupRect.width / 2;

        const margin = 16;
        const maxLeft = window.innerWidth - popupRect.width - margin;
        const minLeft = margin;
        const actualLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));

        this.popup.style.top = `${top}px`;
        this.popup.style.left = `${actualLeft}px`;

        const arrowLeft = triggerCenter - actualLeft;
        popupContent.style.setProperty("--arrow-left", `${arrowLeft}px`);
        popupContent.style.setProperty("--arrow-position", position);
      }

      open() {
        this.cancelClose();

        document
          .querySelectorAll(".info-popover__popup:not([hidden])")
          .forEach((popup) => {
            if (popup !== this.popup) {
              popup.setAttribute("hidden", "");
              const otherTrigger = document.querySelector(
                `[data-popover-trigger="${popup.dataset.popover}"]`
              );
              if (otherTrigger) {
                otherTrigger.setAttribute("aria-expanded", "false");
              }
            }
          });

        this.trigger.setAttribute("aria-expanded", "true");
        this.popup.removeAttribute("hidden");

        requestAnimationFrame(() => {
          this.positionPopup();
        });
      }

      close() {
        this.cancelClose();
        this.trigger.setAttribute("aria-expanded", "false");
        this.popup.setAttribute("hidden", "");
      }

      scheduleClose() {
        this.closeTimeout = setTimeout(() => this.close(), 100);
      }

      cancelClose() {
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = null;
        }
      }
    }
  );
}
